import type { Building } from "building-types/building/buildingTypes";
import type { SourcedFloorAssemblyFinishLayerOperator } from "building-types/floors/floorFinishTypes";
import type { FloorFinishAssemblyTable, FloorSystem } from "building-types/floors/floorSystemTypes";
import type { Floor } from "building-types/floors/floorTypes";
import type { GeneratedFoundationSuperSystem } from "building-types/foundations/foundationTypes";
import type { FloorFinishHandle } from "building-types/handles/docEntityHandleTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import type { Room } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { RWTable, Table } from "common/data/table";
import type { Mutable } from "common/utils/types";
import uid from "common/utils/uid";
import { finalizeFoundationComponents } from "doc/elements/foundation/foundationComponentsTools";
import { foundationSystemSettingsFromSettingsConfig } from "doc/elements/foundation/foundationSettingsTools";
import { mergedPropLibraryNew } from "doc/propTools";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { makeRoomFloorFinishAssembly } from "../elements/floor/floorFinishAssembly";
import {
  buildPropOutlineCache,
  collectOpenConnectedRoomIds,
  filterCaseworkForOpenConnectedRooms,
  filterPropsForOpenConnectedRooms,
  makeCaseworkFloorFinishLayerOperators,
  makeFloorFinishThresholdOperators,
  makePropFloorFinishLayerOperators,
} from "../elements/floor/floorFinishOperators";
import { makeFloors, makeFloorFinishes, floorSystemNew } from "../elements/floor/floorSystem";
import { makeFoundationSuperSystemData } from "../elements/foundation/foundationSuperSystem";
import getTargetedPorchSettings from "../elements/porch/getTargetedPorchSettings";
import { resolvedFoundationSuperSystemAdjacencies } from "../generationTools/generateFloorSystemsTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import { memoAsync } from "../utils/memo";

export function select({
  doc: {
    assets,
    settings: docSettings,
    entitySettings,
    foundationComponents,
    frameFloorAssemblies,
    framingComponents,
    slabComponents,
    sheathingComponents,
  },
  rooms,
  wallMasses,
  walls,
  settings,
  settingsSets,
  optionChoices,
  casework,
  props,
  levels,
  roomsByLevel,
  roomOutlineTable,
  roomLevelIndices,
  roomFloorAssemblies,
  roomFloorFinishes,
  roomClassFloorAssemblyGroups,
  planarGraphs,
  planarGraphLevelAssociations,
  interiorPalette,
  exteriorPalette,
  floorPaths,
  foundationPaths,
}: Pick<
  Building,
  | "doc"
  | "rooms"
  | "wallMasses"
  | "walls"
  | "settings"
  | "settingsSets"
  | "optionChoices"
  | "casework"
  | "props"
  | "levels"
  | "roomsByLevel"
  | "roomOutlineTable"
  | "roomLevelIndices"
  | "roomFloorAssemblies"
  | "roomFloorFinishes"
  | "roomClassFloorAssemblyGroups"
  | "planarGraphs"
  | "planarGraphLevelAssociations"
  | "interiorPalette"
  | "exteriorPalette"
  | "floorPaths"
  | "foundationPaths"
>) {
  return {
    assets,
    levels,
    rooms,
    roomsByLevel,
    roomOutlineTable,
    roomLevelIndices,
    roomFloorAssemblies,
    roomFloorFinishes,
    roomClassFloorAssemblyGroups,
    walls,
    wallMasses,
    wallSettings: settings.wall,
    porchSettings: settings.porch,
    rearPorchSettings: settings.rearPorch ?? settings.porch,
    columnSettings: settings.column,
    foundationSettings: settings.foundation,
    foundationComponents,
    casework,
    frameFloorAssemblies,
    framingComponents,
    slabComponents,
    sheathingComponents,
    props,
    propCatalog: settings.props,
    interiorPalette,
    exteriorPalette,
    planarGraphs,
    planarGraphLevelAssociations,
    floorPaths,
    foundationPaths,
    settingsSets,
    optionChoices,
    docSettings,
    entitySettings,
    settings,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
});

interface GenerateFloorSystemsResult {
  floorSystems: Building["floorSystems"];
  foundationSuperSystems: Table<GeneratedFoundationSuperSystem>;
  issues: BuildingGenerationIssue[];
}

async function generate(props: ReturnType<typeof select>): Promise<GenerateFloorSystemsResult> {
  return makeFloorSystems(props);
}

export default memoAsync(select, generate, compare);

export async function makeFloorSystems({
  assets,
  levels,
  rooms,
  roomOutlineTable,
  roomsByLevel,
  roomLevelIndices,
  roomFloorAssemblies,
  roomFloorFinishes,
  roomClassFloorAssemblyGroups,
  planarGraphs,
  planarGraphLevelAssociations,
  walls,
  wallMasses,
  wallSettings,
  porchSettings,
  rearPorchSettings,
  columnSettings,
  foundationSettings,
  foundationComponents,
  casework,
  frameFloorAssemblies,
  framingComponents,
  slabComponents,
  sheathingComponents,
  props,
  propCatalog,
  settingsSets,
  optionChoices,
  docSettings,
  entitySettings,
  interiorPalette,
  exteriorPalette,
  floorPaths,
  foundationPaths,
  settings,
}: ReturnType<typeof select>): Promise<GenerateFloorSystemsResult> {
  const foundationSystemSettings = foundationSystemSettingsFromSettingsConfig(foundationSettings);
  const finalizedFoundationComponents = finalizeFoundationComponents(foundationComponents, foundationSystemSettings);

  const floorFinishAssemblies: Mutable<FloorFinishAssemblyTable> = {};
  const floorSystems: RWTable<FloorSystem> = {};
  const foundationSuperSystems: RWTable<GeneratedFoundationSuperSystem> = {};
  const mergedPropLibrary = mergedPropLibraryNew(assets);
  const issues: BuildingGenerationIssue[] = [];
  const targetedPorchSettings = getTargetedPorchSettings(
    { settingsSets, optionChoices, settings, rooms },
    { settings: docSettings, entitySettings },
  );
  const floorSystemSettings = {
    wallSettings,
    porchSettings,
    rearPorchSettings,
    columnSettings,
    foundationSettings,
  } as const;

  const openConnectedRoomIds = collectOpenConnectedRoomIds(Object.keys(levels), wallMasses);
  const propsInOpenConnectedRooms = filterPropsForOpenConnectedRooms(props, openConnectedRoomIds);
  const propOutlineCache = await buildPropOutlineCache(propsInOpenConnectedRooms, propCatalog, mergedPropLibrary);

  for (const levelId in levels) {
    const wallIndex = walls[levelId].wallIndex;
    const roomSets = wallMasses[levelId].openConnectedRoomSets;
    for (const roomSet of roomSets) {
      const openConnectedRoomOutlines = new Map<string, Room["outline"]>();
      for (const room of roomSet) {
        openConnectedRoomOutlines.set(room.id, room.outline);
      }
      const roomSetRoomIds = new Set(openConnectedRoomOutlines.keys());
      const roomSetCasework = filterCaseworkForOpenConnectedRooms(casework, roomSetRoomIds);
      const roomSetProps = filterPropsForOpenConnectedRooms(propsInOpenConnectedRooms, roomSetRoomIds);

      for (const room of roomSet) {
        const roomId = room.id;
        const roomOutlines = roomOutlineTable[roomId];
        const roomWalls = wallIndex.wallsInRoom(room);
        const roomOutline = openConnectedRoomOutlines.get(roomId);
        const finishes = roomFloorFinishes[roomId];
        const mappedFinishes = finishes.map((finish) => {
          const srcHandle: FloorFinishHandle = { type: "FLOOR_FINISH", id: finish.id };
          return { ...finish, srcHandle };
        });

        const finishOperators: SourcedFloorAssemblyFinishLayerOperator[] = [
          ...mappedFinishes,
          ...makeCaseworkFloorFinishLayerOperators(roomSetCasework, roomId, roomOutline),
          ...makePropFloorFinishLayerOperators(roomSetProps, roomId, roomOutline, propOutlineCache),
          ...makeFloorFinishThresholdOperators(room, roomWalls, mappedFinishes),
        ];

        floorFinishAssemblies[roomId] = makeRoomFloorFinishAssembly({
          room,
          roomOutlines,
          roomWalls,
          finishOperators,
        });
      }
    }
  }

  for (const levelId in levels) {
    const levelRooms = roomsOnLevel(levelId, rooms, roomsByLevel);
    const levelGraph = planarGraphs[levelId];
    const levelFloorPaths = floorPaths[levelId];

    if (!levelFloorPaths) {
      throw new Error(`Floor paths not found for level ${levelId}`);
    }

    let floors: Floor[][] = [];

    if (levels[levelId]?.enableAutoGeneratedFloorSystem) {
      floors = makeFloors({
        levelId,
        levels,
        planarGraph: levelGraph,
        planarGraphLevelAssociations: planarGraphLevelAssociations[levelId],
        rooms: levelRooms,
        roomLevelIndices,
        roomFloorAssemblies,
        roomClassFloorAssemblyGroups,
        frameFloorAssemblies,
        framingComponents,
        slabComponents,
        sheathingComponents,
        wallSystem: walls[levelId],
        interiorPalette,
        exteriorPalette,
        floorPaths: levelFloorPaths,
        settings: floorSystemSettings,
        targetedPorchSettings,
      });
    }

    const floorFinishes = makeFloorFinishes({
      levelId,
      levels,
      planarGraph: levelGraph,
      planarGraphLevelAssociations: planarGraphLevelAssociations[levelId],
      rooms: levelRooms,
      roomLevelIndices,
      roomFloorFinishAssemblies: floorFinishAssemblies,
      roomFloorAssemblies,
      roomClassFloorAssemblyGroups,
      wallSystem: walls[levelId],
      interiorPalette,
      exteriorPalette,
      frameFloorAssemblies,
      framingComponents,
      slabComponents,
      sheathingComponents,
      floorPaths: levelFloorPaths,
      settings: floorSystemSettings,
      targetedPorchSettings,
    });

    floorSystems[levelId] = floorSystemNew({
      levelId,
      roomClassFloors: floors,
      roomFloorFinishes: floorFinishes,
    });

    if (foundationPaths && foundationPaths[levelId] && levels[levelId]?.enableAutoGeneratedFloorSystem) {
      const level = levels[levelId];

      const foundationSuperSystemId = uid();
      foundationSuperSystems[foundationSuperSystemId] = makeFoundationSuperSystemData(
        {
          level,
          levels,
          planarGraph: levelGraph,
          roomClassFloors: floors,
          foundationSettings: foundationSystemSettings,
          foundationComponents: finalizedFoundationComponents,
          framingComponents,
          slabComponents,
          sheathingComponents,
          issues,
          floorPaths: levelFloorPaths,
        },
        foundationPaths[levelId],
        foundationSuperSystemId,
        levelRooms.length > 0 ? roomEntityHandleForSelectingReportingUnit(levelRooms[0]) : undefined,
      );
    }
  }
  return {
    floorSystems,
    foundationSuperSystems: resolvedFoundationSuperSystemAdjacencies(foundationSuperSystems),
    issues,
  };
}
